import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { centsToDollars, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { useMemo, useState } from "react";
import {
  useGetPlineReconAccountsQuery,
  useLazyGetPlineReconciliationQuery,
  useLazyGetPlineReconciliationWorkbookQuery,
} from "store/queries/plineReconciliation";
import styled from "styled-components";
import {
  IPlineCycle,
  IPlineReconciliation,
  IPlineReconciliationArgs,
} from "types/PlineReconciliation";

// CLE-2917: finance's P-line / GL reconciliation for one P-line facility over a range of report dates.
// SuperAdmin only. Nothing loads until "Run reconciliation" is clicked.

export const formatMoney = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return "";
  const text = parsePrice(centsToDollars(Math.abs(cents)));
  return cents < 0 ? `(${text})` : text;
};

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

export const defaultRange = (now: Date = new Date()) => {
  const from = new Date(now.getTime());
  from.setUTCDate(from.getUTCDate() - 90);
  return { from: isoDay(from), to: isoDay(now) };
};

const StyledContainer = styled.div`
  min-width: 0;
  max-width: 100%;

  .controls label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.85rem;
  }

  .summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
  }

  .figure {
    border: 1px solid var(--gray-200, #e5e7eb);
    border-radius: 6px;
    padding: 8px 12px;
  }

  .figure .value {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .cycle {
    border: 1px solid var(--gray-200, #e5e7eb);
    border-radius: 8px;
    padding: 12px;
    margin-top: 12px;
  }

  .balanced {
    color: #166534;
    background: #e3f4e1;
  }

  .unbalanced {
    color: #991b1b;
    background: #fde2e1;
  }

  .pill {
    border-radius: 999px;
    padding: 2px 10px;
    font-weight: 600;
  }

  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
  }

  table {
    border-collapse: collapse;
    font-size: 0.85rem;
    width: max-content;
    min-width: 100%;
  }

  th,
  td {
    border-bottom: 1px solid var(--gray-200, #e5e7eb);
    padding: 4px 8px;
    text-align: left;
    white-space: nowrap;
  }

  td.money,
  th.money {
    text-align: right;
  }

  .explanations li {
    margin-bottom: 4px;
  }
`;

function CycleBlock({ cycle }: { cycle: IPlineCycle }) {
  return (
    <section
      className="cycle"
      data-testid={`pline-cycle-${cycle.reportDate}`}
      aria-label={`Cycle ${cycle.reportDate}`}
    >
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <h4 className="m-0">Report date {cycle.reportDate}</h4>
        <span
          className={`pill ${cycle.balanced ? "balanced" : "unbalanced"}`}
          data-testid="cycle-balance"
        >
          {cycle.balanced
            ? "Balanced"
            : `Out of balance: ${formatMoney(cycle.balanceInCents)}`}
        </span>
      </div>
      <div className="summary mt-2">
        <div className="figure">
          <div>P-line total</div>
          <div className="value">{formatMoney(cycle.plineTotalInCents)}</div>
        </div>
        <div className="figure">
          <div>Cash paid at facility</div>
          <div className="value">
            {formatMoney(cycle.cashAtFacilityInCents)}
          </div>
        </div>
        <div className="figure">
          <div>Payouts from Clear</div>
          <div className="value">{formatMoney(cycle.payoutTotalInCents)}</div>
        </div>
        <div className="figure">
          <div>Balance</div>
          <div
            className={`value ${cycle.balanced ? "balanced" : "unbalanced"}`}
          >
            {formatMoney(cycle.balanceInCents)}
          </div>
        </div>
        <div className="figure">
          <div>Cumulative balance</div>
          <div className="value">
            {formatMoney(cycle.cumulativeBalanceInCents)}
          </div>
        </div>
      </div>

      {cycle.explanations.length > 0 && (
        <div className="mt-2">
          <strong>Why this cycle is out of balance</strong>
          <ul className="explanations">
            {cycle.explanations.map((e) => (
              <li key={`${e.code}-${e.message}`}>
                <code>{e.code}</code>
                {e.effectInCents !== null &&
                  ` (${formatMoney(e.effectInCents)})`}
                : {e.message}
                {e.orderIds.length > 0 && ` Orders: ${e.orderIds.join(", ")}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <h5 className="mb-1">P-Line Submissions</h5>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Unit Number</th>
              <th>Procedure Code</th>
              <th className="money">Amount</th>
              <th>Date</th>
              <th>SN Comment</th>
              <th>Order ID</th>
            </tr>
          </thead>
          <tbody>
            {cycle.plineRows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`${r.reportId}-${i}`}>
                <td>{r.unitNumber}</td>
                <td>{r.procedureCode}</td>
                <td className="money">{formatMoney(r.amountInCents)}</td>
                <td>{r.date}</td>
                <td>{r.snComment}</td>
                <td>{r.orderId ?? ""}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={2}>
                <strong>AR Total</strong>
              </td>
              <td className="money">
                <strong>{formatMoney(cycle.plineTotalInCents)}</strong>
              </td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid mt-2">
        <div className="col-12 md:col-6">
          <h5 className="mb-1">Cash Paid at Facility</h5>
          {cycle.cash.length === 0 ? (
            <div>None</div>
          ) : (
            <table>
              <tbody>
                {cycle.cash.map((c) => (
                  <tr key={c.paymentId}>
                    <td>{c.orderId}</td>
                    <td>{c.status}</td>
                    <td className="money">{formatMoney(c.amountInCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="col-12 md:col-6">
          <h5 className="mb-1">Payouts from Clear</h5>
          {cycle.payouts.length === 0 ? (
            <div>No payout linked</div>
          ) : (
            <table>
              <tbody>
                {cycle.payouts.map((p) => (
                  <tr key={p.id}>
                    <td>{p.externalId}</td>
                    <td>{p.arrivalDate ?? ""}</td>
                    <td>{p.status}</td>
                    <td className="money">{formatMoney(p.amountInCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <h5 className="mb-1">GL Submissions</h5>
      {cycle.glRows.length === 0 ? (
        <div>No GL rows</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>COID</th>
                <th>Account</th>
                <th>Description</th>
                <th className="money">Debit</th>
                <th className="money">Credit</th>
                <th>PeriodEnd</th>
              </tr>
            </thead>
            <tbody>
              {cycle.glRows.map((g) => (
                <tr key={`${g.reportId}-${g.account}-${g.periodEnd}`}>
                  <td>{g.coid}</td>
                  <td>{g.account}</td>
                  <td>{g.description}</td>
                  <td className="money">{formatMoney(g.debitInCents)}</td>
                  <td className="money">{formatMoney(g.creditInCents)}</td>
                  <td>{g.periodEnd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Results({ recon }: { recon: IPlineReconciliation }) {
  return (
    <div data-testid="pline-reconciliation-results">
      <div className="summary mt-3">
        <div className="figure">
          <div>P-line total</div>
          <div className="value">
            {formatMoney(recon.totals.plineTotalInCents)}
          </div>
        </div>
        <div className="figure">
          <div>Cash paid at facility</div>
          <div className="value">
            {formatMoney(recon.totals.cashAtFacilityInCents)}
          </div>
        </div>
        <div className="figure">
          <div>Payouts from Clear</div>
          <div className="value">
            {formatMoney(recon.totals.payoutTotalInCents)}
          </div>
        </div>
        <div className="figure">
          <div>Balance</div>
          <div
            className={`value ${
              recon.totals.balanceInCents === 0 ? "balanced" : "unbalanced"
            }`}
            data-testid="total-balance"
          >
            {formatMoney(recon.totals.balanceInCents)}
          </div>
        </div>
        <div className="figure">
          <div>GL debit / credit</div>
          <div className="value">
            {formatMoney(recon.totals.glDebitInCents)} /{" "}
            {formatMoney(recon.totals.glCreditInCents)}
          </div>
        </div>
      </div>
      <p className="mt-2">
        {recon.cycleCount} weekly cycle(s), {recon.unbalancedCycleCount} out of
        balance. Balance = P-line total − Cash paid at facility − Payouts from
        Clear.
      </p>
      {recon.notes.map((n) => (
        <p key={n}>{n}</p>
      ))}
      {recon.cycles.length === 0 && (
        <p>No P-line reports are dated in this range.</p>
      )}
      {recon.cycles.map((c) => (
        <CycleBlock key={c.reportDate} cycle={c} />
      ))}
    </div>
  );
}

export function PlineReconciliationPanel() {
  const { data: accountsResponse, isLoading: accountsLoading } =
    useGetPlineReconAccountsQuery();
  const [run, { data, isFetching, error }] =
    useLazyGetPlineReconciliationQuery();
  const [fetchWorkbook, { isFetching: downloading }] =
    useLazyGetPlineReconciliationWorkbookQuery();
  const initial = useMemo(() => defaultRange(), []);
  const [accountId, setAccountId] = useState("");
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The LPNT / ScionHealth aggregate accounts have no payouts of their own; reconcile the facility instead.
  const accounts = (accountsResponse?.data ?? []).filter((a) => !a.isAggregate);
  const args: IPlineReconciliationArgs = { accountId, from, to };
  const ready = Boolean(accountId && from && to && from <= to);
  // A failed run must not leave the previous result on screen as if it were the answer.
  const recon = error ? undefined : data?.data;

  const downloadExcel = async () => {
    if (!recon) return;
    setDownloadError(null);
    try {
      // The workbook is the reconciliation on screen — its own account and range, whatever the form says now.
      const { blob, fileName } = await fetchWorkbook({
        accountId: recon.account.id,
        from: recon.from,
        to: recon.to,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setDownloadError(e?.data?.message ?? "The workbook could not be built.");
    }
  };

  return (
    <StyledContainer data-testid="pline-reconciliation">
      <h3 className="mt-0">P-line / GL reconciliation</h3>
      <p className="mt-0">
        For a P-line facility, each weekly report&apos;s P-line submissions, the
        cash collected at the facility, the payouts from Clear and the GL lines,
        with the balance per week and why it is not zero.
      </p>
      <div className="controls flex flex-wrap align-items-end gap-3">
        <label htmlFor="pline-recon-account">
          Facility
          <select
            id="pline-recon-account"
            className="p-inputtext"
            value={accountId}
            disabled={accountsLoading}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Select a P-line facility</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.firstReportDate} to {a.lastReportDate})
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="pline-recon-from">
          Report dates from
          <input
            id="pline-recon-from"
            type="date"
            className="p-inputtext"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label htmlFor="pline-recon-to">
          to
          <input
            id="pline-recon-to"
            type="date"
            className="p-inputtext"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        <Button
          label="Run reconciliation"
          disabled={!ready}
          loading={isFetching}
          onClick={() => run(args)}
        />
        {recon && (
          <Button
            label="Download Excel"
            icon="pi pi-download"
            className="p-button-outlined"
            loading={downloading}
            onClick={downloadExcel}
          />
        )}
      </div>
      {error && (
        <p className="unbalanced" role="alert">
          {(error as any)?.data?.message ??
            "The reconciliation could not be built."}
        </p>
      )}
      {downloadError && (
        <p className="unbalanced" role="alert">
          {downloadError}
        </p>
      )}
      {recon && <Results recon={recon} />}
    </StyledContainer>
  );
}

export function PlineReconciliation() {
  const { userInfo } = useUserWithAccount();
  if (userInfo?.scope !== UserRole.SUPER_ADMIN) return null;
  return <PlineReconciliationPanel />;
}
